import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { forkJoin, Observable, of } from "rxjs";
import { catchError, delay, map, shareReplay, startWith, switchMap } from "rxjs/operators";

import { UmbracoHttpClient } from "@e-forsyning/common/http";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";

import {
    AdressevaelgerAdresseLookupDto,
    AdressevaelgerFundDto,
    AdressevaelgerHusnummerDto,
    AdressevaelgerHusnummerLookupDto,
    AdressevaelgerSoegResponseDto,
    CheckOmDerLeveresPaaAdressenResultDto,
    ForsyningPaaAdresseKonfigurationDto
} from "../model/forsyning-paa-adresse.dto";
import { Adresse, AdresseSearch, ForsyningPaaAdresseKonfiguration, ForsyningPaaAdresseLeveringStatus, ForsyningPaaAdresseResult } from "../model/forsyning-paa-adresse.model";

const ADRESSEVAELGER_BASE_URL = "https://adressevaelger.dk";
const ADRESSEVAELGER_TOKEN = "adressevaelger123";

@Injectable({ providedIn: "root" })
export class ForsyningPaaAdresseRepository {
    constructor(
        private readonly indstillinger: UmbracoIndstillingerObserver,
        private readonly httpClient: HttpClient,
        private readonly umbracoHttpClient: UmbracoHttpClient
    ) {}

    public selectKonfiguration(nodeId: number): Observable<ForsyningPaaAdresseKonfiguration> {
        return this.umbracoHttpClient.hentSideDataForId<ForsyningPaaAdresseKonfigurationDto>(nodeId).pipe(
            map((dto) => ({
                eforsyningNodeId: +dto.eforsyningNodeId,
                svarmuligheder: {
                    ja: dto.svarmulighedJa,
                    nej: dto.svarmulighedNej,
                    snart: dto.svarmulighedPlanlagt
                },
                afgraensning: {
                    postNr: (dto.afgraensningPostNr?.split("\n") ?? []).filter((p) => !!p)
                },
                cssLeveresFjernvarme: dto.cssLeveresFjernvarme
            }))
        );
    }

    public findAdresser(value: string, postNr: string[]): Observable<AdresseSearch[]> {
        const trimmed = value.trim();
        if (!trimmed) {
            return of([]);
        }

        if (postNr?.length) {
            const parsed = this.parseSoegetekst(trimmed);
            return forkJoin(postNr.map((pnr) => this.soegMedPostnummer(parsed.vejnavn, pnr, parsed.husnummer))).pipe(
                map((resultater) => this.deduplikerFund([].concat(...resultater))),
                catchError(() => of([]))
            );
        }

        const params = new HttpParams().set("tekst", trimmed).set("token", ADRESSEVAELGER_TOKEN);
        return this.httpClient.get<AdressevaelgerSoegResponseDto>(`${ADRESSEVAELGER_BASE_URL}/adresser/soeg`, { params }).pipe(
            map((response) => this.mapSoegTilAdresseSearch(response, [])),
            catchError(() => of([]))
        );
    }

    public checkOmDerLeveresPaaAdressen(eforsyningNodeId: number, sel: AdresseSearch): Observable<ForsyningPaaAdresseResult> {
        return this.resolveAdresse(sel).pipe(
            switchMap((adresse) => {
                if (!adresse) {
                    return of(null);
                }
                const resolved: AdresseSearch = { ...sel, adresse };
                return this.indstillinger.valueChanges.pipe(
                    map((i) => i.eforsyninger.find((e) => e.nodeId === eforsyningNodeId)),
                    switchMap((e) => this.httpCheckOmDerLeveresPaaAdressen(e.appServerUrl, resolved)),
                    delay(1000),
                    startWith({ ejendomNr: 0, status: ForsyningPaaAdresseLeveringStatus.Arbejder, statusTekst: null, tekst: null }),
                    shareReplay(1)
                );
            })
        );
    }

    private soegMedPostnummer(vejnavn: string, postnummer: string, husnummer?: string): Observable<AdresseSearch[]> {
        let params = new HttpParams().set("vejnavn", vejnavn).set("postnummer", postnummer).set("token", ADRESSEVAELGER_TOKEN);
        if (husnummer) {
            params = params.set("husnummer", husnummer);
        }

        return this.httpClient.get<AdressevaelgerSoegResponseDto>(`${ADRESSEVAELGER_BASE_URL}/adresser/soeg`, { params }).pipe(
            map((response) => this.mapSoegTilAdresseSearch(response, [postnummer], true)),
            catchError(() => of([]))
        );
    }

    private parseSoegetekst(value: string): { vejnavn: string; husnummer?: string } {
        const match = value.match(/^(.+?)\s+(\d+[\wæøåÆØÅ]*)$/u);
        if (match) {
            return { vejnavn: match[1].trim(), husnummer: match[2] };
        }
        return { vejnavn: value };
    }

    private resolveAdresse(sel: AdresseSearch): Observable<Adresse | null> {
        if (sel.adresse) {
            return of(sel.adresse);
        }
        if (!sel.id || !sel.type || sel.type === "navngivenvejpostnummer") {
            return of(null);
        }

        const params = new HttpParams().set("token", ADRESSEVAELGER_TOKEN);
        if (sel.type === "adresse") {
            return this.httpClient
                .get<AdressevaelgerAdresseLookupDto>(`${ADRESSEVAELGER_BASE_URL}/adresser/${sel.id}`, { params })
                .pipe(
                    map((response) => (response.status === "ok" && response.adresse ? this.mapAdresseDto(response.adresse) : null)),
                    catchError(() => of(null))
                );
        }

        return this.httpClient
            .get<AdressevaelgerHusnummerLookupDto>(`${ADRESSEVAELGER_BASE_URL}/husnumre/${sel.id}`, { params })
            .pipe(
                map((response) => (response.status === "ok" && response.husnummer ? this.mapHusnummerDto(response.husnummer) : null)),
                catchError(() => of(null))
            );
    }

    private mapSoegTilAdresseSearch(response: AdressevaelgerSoegResponseDto, postNr: string[], visMellemresultater = false): AdresseSearch[] {
        if (response.status !== "ok" || !response.fund?.length) {
            return [];
        }

        return response.fund
            .filter((fund) => this.erSynligFund(fund, visMellemresultater))
            .filter((fund) => this.matcherPostNr(fund, postNr))
            .map((fund) => this.mapFundTilAdresseSearch(fund));
    }

    private erSynligFund(fund: AdressevaelgerFundDto, visMellemresultater: boolean): boolean {
        if (!fund.titel?.trim()) {
            return false;
        }
        if (fund.type === "adresse" || fund.type === "husnummer") {
            return !!fund.id;
        }
        return visMellemresultater && fund.type === "navngivenvejpostnummer";
    }

    private mapFundTilAdresseSearch(fund: AdressevaelgerFundDto): AdresseSearch {
        return {
            tekst: fund.titel,
            id: fund.id,
            type: fund.type as AdresseSearch["type"],
            vejnavn: fund.vejnavn
        };
    }

    private matcherPostNr(fund: AdressevaelgerFundDto, postNr: string[]): boolean {
        if (!postNr?.length) {
            return true;
        }
        const fundPostnr = fund.postnr ?? this.postnrFraTitel(fund.titel);
        return fundPostnr ? postNr.includes(fundPostnr) : false;
    }

    private postnrFraTitel(titel: string): string | null {
        const sidsteDel = titel.split(",").pop()?.trim() ?? titel.trim();
        return sidsteDel.match(/(\d{4})/)?.[1] ?? null;
    }

    private deduplikerFund(fund: AdresseSearch[]): AdresseSearch[] {
        const seen = new Set<string>();
        return fund.filter((item) => {
            const key = item.id ? `${item.type}:${item.id}` : item.tekst;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }

    private mapAdresseDto(dto: { etagebetegnelse?: string | null; doerbetegnelse?: string | null; husnummer?: AdressevaelgerHusnummerDto }): Adresse {
        const husnummer = dto.husnummer;
        const parsed = this.parseHusnummerTekst(husnummer?.husnummertekst ?? "");
        return {
            kommunenr: this.parseKommunenr(husnummer?.navngivenvejkommunedel?.kommune),
            vejnr: +(husnummer?.navngivenvejkommunedel?.vejkode ?? 0),
            husnr: parsed.husnr,
            husnrBogstav: parsed.husnrBogstav,
            etage: dto.etagebetegnelse ?? "",
            doer: dto.doerbetegnelse ?? "",
            postnr: husnummer?.postnummer?.postnr ?? ""
        };
    }

    private mapHusnummerDto(dto: AdressevaelgerHusnummerDto): Adresse {
        const parsed = this.parseHusnummerTekst(dto.husnummertekst ?? "");
        return {
            kommunenr: this.parseKommunenr(dto.navngivenvejkommunedel?.kommune),
            vejnr: +(dto.navngivenvejkommunedel?.vejkode ?? 0),
            husnr: parsed.husnr,
            husnrBogstav: parsed.husnrBogstav,
            etage: "",
            doer: "",
            postnr: dto.postnummer?.postnr ?? ""
        };
    }

    private parseHusnummerTekst(husnummer: string): { husnr: number; husnrBogstav: string } {
        const match = husnummer.match(/^(\d+)(.*)$/);
        return {
            husnr: match ? +match[1] : 0,
            husnrBogstav: match?.[2] ?? ""
        };
    }

    private parseKommunenr(kommune: string | undefined): number {
        if (!kommune) {
            return 0;
        }
        return +kommune;
    }

    private httpCheckOmDerLeveresPaaAdressen(appServerUrl: string, adresse: AdresseSearch): Observable<ForsyningPaaAdresseResult> {
        if (!adresse?.adresse) {
            return of(null);
        }
        const etage = adresse.adresse.etage || "";
        const doer = adresse.adresse.doer || "";
        const url = `${appServerUrl}api/LeveresFjernvarmePaaAdresse?kommunenr=${adresse.adresse.kommunenr}&vejnr=${adresse.adresse.vejnr}&husnr=${adresse.adresse.husnr}&husnrBogstav=${adresse.adresse.husnrBogstav}&etage=${etage}&doer=${doer}`;
        return this.httpClient.get<CheckOmDerLeveresPaaAdressenResultDto>(url).pipe(map((result) => this.mapForsyningPaaAdresseResult(result, adresse.tekst)));
    }

    private mapForsyningPaaAdresseResult(dto: CheckOmDerLeveresPaaAdressenResultDto, adresseTekst: string): ForsyningPaaAdresseResult {
        return {
            ejendomNr: +(dto.ejendomNr ?? 1),
            status: +dto.status as ForsyningPaaAdresseLeveringStatus,
            statusTekst: +dto.status === ForsyningPaaAdresseLeveringStatus.LevererIFremtiden ? dto.tekst : null,
            tekst: adresseTekst
        };
    }
}
